import React, { useState, useEffect } from "react";
import {
  Layout,
  Tabs,
  Card,
  List,
  Avatar,
  Input,
  Button,
  Space,
  Typography,
  Form,
  Select,
  message as antdMessage,
  Tag,
  Modal,
} from "antd";
import axios from "axios";
import {
  UserOutlined,
  RobotOutlined,
  SendOutlined,
  PlusOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import "antd/dist/reset.css";
import "./App.css";

const { Header, Content } = Layout;
const { Text, Title } = Typography;
const { TextArea } = Input;

type Locale = "en" | "zh";

const i18nMessages: Record<Locale, Record<string, string>> = {
  en: {
    appTitle: "WhatsApp Tech Agent Console",
    tabWaba: "WABA Registration",
    tabChat: "Send Messages",
    tabTemplates: "Template Management",

    contactListTitle: "Contacts",
    chatCardTitle: "Send and Receive WhatsApp Messages",
    chatWindowSubtitle: "WhatsApp Chat Window",
    sendingTo: "Sending to:",
    messageLabel: "Message",
    messagePlaceholder:
      "Enter the message to send to the WhatsApp user, or select a template.",
    sendButton: "Send Message",
    sendSuccess: "Message sent via WhatsApp API.",
    sendFailed: "Failed to send WhatsApp message. Please check configuration.",
    quickTemplateTitle: "Quickly select an existing template",

    templateCardTitle: "Template Management (Create / View / Use)",
    templateCreateTitle: "1. Create new message template",
    templateListTitle: "2. Template list",
    templateNameLabel: "Template name",
    templateNamePlaceholder: "e.g. Order confirmation / Promotion",
    templateCategoryLabel: "Template category",
    templateLanguageLabel: "Language",
    templateBodyLabel: "Template body",
    templateBodyPlaceholder:
      "Enter the message template content, e.g. your order has been confirmed...",
    templateCreateButton: "Create template",
    templateSyncButton: "Sync templates from current WABA",
    templateCreateSuccess: "Template created.",
    templateUseButton: "Use this template to send message",

    wabaNotice: "Please complete Marketing Message Lite registration.",
    wabaTabInternal: "WABA registration and binding",
    wabaTabMigrate: "Number migration",
    wabaNumberManageTitle: "Number management",
    wabaBindButton: "Register / Authorize WhatsApp number",

    accessTokenLabel: "Graph API Access Token",
    syncPhoneButton: "Sync phone number info",
  },
  zh: {
    appTitle: "WhatsApp 技术代理管理页面",
    tabWaba: "WABA 注册",
    tabChat: "发送消息",
    tabTemplates: "模板管理",

    contactListTitle: "联系人列表",
    chatCardTitle: "发送与接收 WhatsApp 消息",
    chatWindowSubtitle: "WhatsApp 聊天窗口",
    sendingTo: "正在给联系人：",
    messageLabel: "消息内容",
    messagePlaceholder:
      "在这里输入要发送给 WhatsApp 用户的消息内容，或者通过模板一键生成。",
    sendButton: "发送消息",
    sendSuccess: "消息已通过 WhatsApp 接口发送。",
    sendFailed: "发送 WhatsApp 消息失败，请检查配置或稍后重试。",
    quickTemplateTitle: "快速选择一个已有模板",

    templateCardTitle: "模板管理（创建 / 查看 / 使用模板）",
    templateCreateTitle: "1. 创建新的消息模板",
    templateListTitle: "2. 模板列表",
    templateNameLabel: "模板名称",
    templateNamePlaceholder: "例如：订单确认 / 营销活动",
    templateCategoryLabel: "模板类型（类别）",
    templateLanguageLabel: "语言",
    templateBodyLabel: "模板正文内容",
    templateBodyPlaceholder:
      "在这里输入消息模板内容，例如：您好，您的订单已确认，我们将尽快为您发货。",
    templateCreateButton: "创建模板",
    templateSyncButton: "从当前授权账号同步模板",
    templateCreateSuccess: "模板创建成功",
    templateUseButton: "使用此模板发送消息",

    wabaNotice: "请完成 Marketing Message Lite 注册。",
    wabaTabInternal: "WABA 注册及绑定",
    wabaTabMigrate: "号码迁移",
    wabaNumberManageTitle: "号码管理",
    wabaBindButton: "注册 / 授权 WhatsApp 号码",

    accessTokenLabel: "Graph API Access Token",
    syncPhoneButton: "同步号码信息",
  },
};

type ChatMessage = {
  id: number;
  from: "business" | "customer";
  content: string;
  time: string;
};

type Contact = {
  id: number;
  name: string;
  phone: string;
  lastMessage: string;
};

type Template = {
  id: number;
  name: string;
  category: string;
  language: string;
  body: string;
  graphId?: string;
};

type WabaBinding = {
  wabaId: string;
  phoneNumberId: string;
  createdAt: number;
};

type WabaPhoneNumber = {
  id: string;
  cc?: string;
  country_dial_code?: string;
  display_phone_number?: string;
  verified_name?: string;
  status?: string;
  quality_rating?: string;
  search_visibility?: string;
  platform_type?: string;
  code_verification_status?: string;
};

const initialTemplates: Template[] = [
  {
    id: 1,
    name: "订单确认",
    category: "TRANSACTIONAL",
    language: "zh_CN",
    body: "您好，您的订单已经确认，我们将尽快为您发货。",
  },
  {
    id: 2,
    name: "营销活动",
    category: "MARKETING",
    language: "zh_CN",
    body: "您好，这是我们的最新活动：全场商品 8 折优惠，欢迎选购！",
  },
];

const getTimeString = () => {
  const d = new Date();
  return d.toLocaleTimeString();
};

function App() {
  const [locale, setLocale] = useState<Locale>("en");
  const t = i18nMessages[locale];
  const [activeTab, setActiveTab] = useState<string>("waba");
  const [wabaBindings, setWabaBindings] = useState<WabaBinding[]>(() => {
    try {
      const raw = localStorage.getItem("waba_bindings");
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? (parsed as WabaBinding[]) : [];
    } catch {
      return [];
    }
  });
  const [graphAccessToken, setGraphAccessToken] = useState<string>(() => {
    const hardcoded =
      "EAAXAH5hR1pkBQZCNriOuOI72mpN8ZAVNiuZBa2HFkl4dNZCyyLmJ4Sq7Ic7HQgt6bIhEYNJdkGLqoNgZBd5QbCcJmfW5MPZA1EqZCpPrsZBBiW5YYapVD8X7RAoafhizYAYmzJawf2uZA2GSgP3QZArvtQvrgCLUBAnmnZBZCBoafQG2rvu1HYFEoKZB21wxwxOZBsNAZDZD";
    try {
      return localStorage.getItem("graph_access_token") ?? hardcoded;
    } catch {
      return hardcoded;
    }
  });
  const [wabaPhoneNumbersByWaba, setWabaPhoneNumbersByWaba] = useState<Record<
    string,
    WabaPhoneNumber[]
  >>(() => {
    try {
      const raw = localStorage.getItem("waba_phone_numbers_by_waba");
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === "object"
        ? (parsed as Record<string, WabaPhoneNumber[]>)
        : {};
    } catch {
      return {};
    }
  });
  // 左侧联系人固定为一个真实号码 17681371015
  const [contacts] = useState<Contact[]>([
    {
      id: 1,
      name: "WhatsApp 用户",
      phone: "17681371015",
      lastMessage: "与该 WhatsApp 用户的会话",
    },
  ]);
  const [selectedContactId, setSelectedContactId] = useState<number>(1);
  const [messagesByContact, setMessagesByContact] = useState<
    Record<number, ChatMessage[]>
  >(() => {
    try {
      const raw = localStorage.getItem("messages_by_contact");
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === "object") {
          return parsed as Record<number, ChatMessage[]>;
        }
      }
    } catch {
      // ignore and use default below
    }
    return {
      1: [
        {
          id: 1,
          from: "customer",
          content: "你好，我这边刚在 WhatsApp 上给你发了一条消息。",
          time: getTimeString(),
        },
        {
          id: 2,
          from: "business",
          content: "您好，这里是客服中心，我们已经收到您的消息。",
          time: getTimeString(),
        },
      ],
    };
  });
  const [inputMessage, setInputMessage] = useState<string>("");
  const [templates, setTemplates] = useState<Template[]>(() => {
    try {
      const raw = localStorage.getItem("templates");
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) {
          return parsed as Template[];
        }
      }
    } catch {
      // ignore and fallback
    }
    return initialTemplates;
  });
  const [templateForm] = Form.useForm<Template>();
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null
  );
  const [sending, setSending] = useState<boolean>(false);
  const [authModalVisible, setAuthModalVisible] = useState<boolean>(false);
  const [selectedAuthType, setSelectedAuthType] = useState<"waba" | "app">(
    "waba"
  );

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) {
      antdMessage.warning(
        locale === "zh"
          ? "请输入要发送的消息内容"
          : "Please enter a message to send."
      );
      return;
    }

    if (!graphAccessToken) {
      antdMessage.error(
        locale === "zh"
          ? "未配置 Graph API Access Token，无法发送 WhatsApp 消息。"
          : "Graph API Access Token is not configured. Cannot send WhatsApp messages."
      );
      return;
    }

    setSending(true);

    const senderPhoneNumberId =
      wabaBindings[0]?.phoneNumberId || "936601246213740";

    const newMsg: ChatMessage = {
      id: Date.now(),
      from: "business",
      content: inputMessage.trim(),
      time: getTimeString(),
    };

    try {
      // 先写入本地会话并持久化
      persistMessagesByContact((prev) => {
        const list = prev[selectedContactId] ?? [];
        return {
          ...prev,
          [selectedContactId]: [...list, newMsg],
        };
      });
      const textBody = inputMessage.trim();
      setInputMessage("");

      // 调用 WhatsApp Cloud API 发送真实消息
      await axios.post(
        `https://graph.facebook.com/v25.0/${encodeURIComponent(
          senderPhoneNumberId
        )}/messages`,
        {
          messaging_product: "whatsapp",
          to: "17681371015",
          type: "text",
          text: {
            body: textBody,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${graphAccessToken}`,
          },
          maxBodyLength: Infinity,
        }
      );

      antdMessage.success(t.sendSuccess);
    } catch (error) {
      console.error(error);
      antdMessage.error(t.sendFailed);
    } finally {
      setSending(false);
    }
  };

  const handleCreateTemplate = (values: Omit<Template, "id">) => {
    const newTemplate: Template = {
      id: Date.now(),
      ...values,
    };
    persistTemplates((prev) => [...prev, newTemplate]);
    antdMessage.success(t.templateCreateSuccess);
    templateForm.resetFields();
  };

  const handleUseTemplate = (tpl: Template) => {
    // 切换到“发送消息”Tab，并把模板内容填入输入框
    setActiveTab("chat");
    setInputMessage(tpl.body);
    antdMessage.info(`已将模板「${tpl.name}」内容填入输入框`);
  };

  const handleOpenAuthModal = () => {
    setAuthModalVisible(true);
  };

  const persistWabaBindings = (updater: (prev: WabaBinding[]) => WabaBinding[]) => {
    setWabaBindings((prev) => {
      const next = updater(prev);
      localStorage.setItem("waba_bindings", JSON.stringify(next));
      return next;
    });
  };

  const persistGraphAccessToken = (next: string) => {
    setGraphAccessToken(next);
    localStorage.setItem("graph_access_token", next);
  };

  const persistMessagesByContact = (
    updater: (prev: Record<number, ChatMessage[]>) => Record<number, ChatMessage[]>
  ) => {
    setMessagesByContact((prev) => {
      const next = updater(prev);
      localStorage.setItem("messages_by_contact", JSON.stringify(next));
      return next;
    });
  };

  const persistTemplates = (updater: (prev: Template[]) => Template[]) => {
    setTemplates((prev) => {
      const next = updater(prev);
      localStorage.setItem("templates", JSON.stringify(next));
      return next;
    });
  };
  const persistWabaPhoneNumbersByWaba = (
    updater: (prev: Record<string, WabaPhoneNumber[]>) => Record<string, WabaPhoneNumber[]>
  ) => {
    setWabaPhoneNumbersByWaba((prev) => {
      const next = updater(prev);
      localStorage.setItem("waba_phone_numbers_by_waba", JSON.stringify(next));
      return next;
    });
  };

  const fetchWabaPhoneNumbers = async (wabaId: string) => {
    if (!graphAccessToken) {
      antdMessage.warning(
        locale === "zh"
          ? "请先填写 Graph API Access Token。"
          : "Please configure Graph API Access Token first."
      );
      return;
    }
    const fields =
      "id,cc,country_dial_code,display_phone_number,verified_name,status,quality_rating,search_visibility,platform_type,code_verification_status";
    const url = `https://graph.facebook.com/v22.0/${encodeURIComponent(
      wabaId
    )}/phone_numbers?fields=${encodeURIComponent(fields)}&access_token=${encodeURIComponent(
      graphAccessToken
    )}`;
    try {
      const res = await fetch(url);
      const json = (await res.json()) as { data?: WabaPhoneNumber[]; error?: any };
      if (!res.ok || json.error) {
        const msg =
          json?.error?.message ||
          `请求失败（HTTP ${res.status}），请检查 token 权限/WABA ID。`;
        antdMessage.error(msg);
        return;
      }
      persistWabaPhoneNumbersByWaba((prev) => ({
        ...prev,
        [wabaId]: Array.isArray(json.data) ? json.data : [],
      }));
      antdMessage.success(
        locale === "zh"
          ? "已同步号码信息。"
          : "Phone number information synced."
      );
    } catch (e) {
      antdMessage.error(
        locale === "zh"
          ? "同步失败，请检查网络或浏览器跨域限制。"
          : "Sync failed, please check network or browser CORS settings."
      );
    }
  };

  // 从当前授权账号（WABA）同步消息模板列表
  const syncTemplatesFromGraph = async () => {
    if (!graphAccessToken) {
      antdMessage.warning("请先填写 Graph API Access Token。");
      return;
    }
    const wabaId = wabaBindings[0]?.wabaId || "770741399423416";
    try {
      const res = await axios.get(
        `https://graph.facebook.com/v19.0/${encodeURIComponent(
          wabaId
        )}/message_templates`,
        {
          headers: {
            Authorization: `Bearer ${graphAccessToken}`,
          },
          maxBodyLength: Infinity,
        }
      );
      const json = res.data as {
        data?: Array<{
          id: string;
          name: string;
          previous_category?: string;
          category?: string;
          language: string;
          components?: Array<{
            type: string;
            text?: string;
          }>;
        }>;
      };
      const apiTemplates = (json.data ?? []).map((tpl, index) => {
        const bodyComponent = tpl.components?.find((c) => c.type === "BODY");
        return {
          id: index + 1,
          name: tpl.name,
          category: tpl.category || tpl.previous_category || "",
          language: tpl.language,
          body: bodyComponent?.text || "",
          graphId: tpl.id,
        } as Template;
      });
      if (apiTemplates.length === 0) {
        antdMessage.info("当前授权账号下暂未查询到消息模板。");
      }
      persistTemplates(() => apiTemplates);
      antdMessage.success("已从当前授权账号同步消息模板。");
    } catch (e) {
      console.error(e);
      antdMessage.error("同步消息模板失败，请检查 WABA ID / Access Token。");
    }
  };

  // 组件加载后，自动从当前授权账号查询模板列表（如果有 token）
  useEffect(() => {
    if (!graphAccessToken) return;
    // 已经有从接口同步过的模板时不再重复自动拉取，保留按钮手动刷新
    const hasGraphTemplates = templates.some((t) => t.graphId);
    if (hasGraphTemplates) return;
    void syncTemplatesFromGraph();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphAccessToken, wabaBindings.length]);

  // 当绑定列表变化且存在 token 时，自动同步对应 WABA 的号码信息
  useEffect(() => {
    const wabaIds = Array.from(new Set(wabaBindings.map((x) => x.wabaId)));
    if (!graphAccessToken || wabaIds.length === 0) return;
    // 只同步本地还没有缓存的 wabaId，减少请求
    const missing = wabaIds.filter((id) => !wabaPhoneNumbersByWaba[id]);
    if (missing.length === 0) return;
    missing.forEach((id) => {
      void fetchWabaPhoneNumbers(id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wabaBindings, graphAccessToken]);

  // 监听 Facebook Embedded Signup 的 postMessage
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) {
        return;
      }
      try {
        const data = JSON.parse(event.data);
        if (data.type === "WA_EMBEDDED_SIGNUP") {
          if (data.event === "FINISH") {
            const { phone_number_id, waba_id } = data.data;
            const nextItem: WabaBinding = {
              wabaId: String(waba_id),
              phoneNumberId: String(phone_number_id),
              createdAt: Date.now(),
            };
            persistWabaBindings((prev) => {
              const exists = prev.some(
                (x) =>
                  x.wabaId === nextItem.wabaId &&
                  x.phoneNumberId === nextItem.phoneNumberId
              );
              return exists ? prev : [nextItem, ...prev];
            });
            antdMessage.success("授权成功，已保存并展示在列表中。");
          } else if (data.event === "CANCEL") {
            const { current_step } = data.data;
            antdMessage.warning(`用户取消授权，步骤：${current_step}`);
          } else if (data.event === "ERROR") {
            const { error_message } = data.data;
            antdMessage.error(`授权错误：${error_message}`);
          }
        }
      } catch {
        // 非 JSON 或解析失败，忽略
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const handleAuthNext = () => {
    if (selectedAuthType === "waba") {
      setAuthModalVisible(false);
      type FBSDK = {
        login: (
          cb: (r: { authResponse?: { code?: string } }) => void,
          opts: object
        ) => void;
      };
      const FB = (window as Window & { FB?: FBSDK }).FB;
      if (!FB) {
        antdMessage.error("Facebook SDK 未加载，请刷新页面后重试。");
        return;
      }
      FB.login(
        (response: { authResponse?: { code?: string } }) => {
          if (response.authResponse?.code) {
            antdMessage.info(
              "已获取授权 code，请将 code 发送到后端换取 access token。"
            );
          }
        },
        {
          config_id: "1484020847061940",
          response_type: "code",
          override_default_response_type: true,
          extras: {
            version: "v3",
            featureType: "whatsapp_business_app_onboarding",
          },
        }
      );
    } else {
      antdMessage.info("当前仅实现 WhatsApp 商业号码（WABA）授权流程。");
    }
  };

  const currentMessages = messagesByContact[selectedContactId] ?? [];

  const chatTab = (
    <Space
      direction="vertical"
      style={{ width: "100%", height: "calc(100vh - 140px)" }}
      size="large"
    >
      <Card
        title={
          <Space>
            <MessageOutlined />
            <span>{t.chatCardTitle}</span>
          </Space>
        }
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >

        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "0.9fr 2.1fr",
            gap: 24,
            marginTop: 24,
            minHeight: 0,
          }}
        >
          {/* 左侧：联系人列表 */}
          <Card size="small" title={t.contactListTitle} style={{ minHeight: 280 }}>
            <List
              dataSource={contacts}
              renderItem={(contact) => (
                <List.Item
                  onClick={() => setSelectedContactId(contact.id)}
                  style={{
                    cursor: "pointer",
                    backgroundColor:
                      contact.id === selectedContactId ? "#e6f4ff" : "transparent",
                    borderRadius: 4,
                    marginBottom: 4,
                    paddingLeft: 8,
                    paddingRight: 8,
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={<UserOutlined />}
                        style={{ backgroundColor: "#87d068" }}
                      />
                    }
                    title={
                      <Space>
                        <Text strong>{contact.name}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {contact.phone}
                        </Text>
                      </Space>
                    }
                    description={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {contact.lastMessage}
                      </Text>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>

          {/* 右侧：聊天窗口（上） + 发送区（下） */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              gap: 16,
              minHeight: 0,
            }}
          >
            {/* 上部分：聊天窗口内容 */}
            <div className="chat-window-header">
              <Text strong style={{ marginRight: 8 }}>
                {contacts.find((c) => c.id === selectedContactId)?.name}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t.chatWindowSubtitle}
              </Text>
            </div>
            <Card
              size="small"
              className="chat-window-card"
              style={{ flex: 1, minHeight: 0, display: "flex" }}
              bodyStyle={{ padding: 12, flex: 1, overflow: "auto" }}
            >
              <List
                dataSource={currentMessages}
                renderItem={(msg) => {
                  const isBusiness = msg.from === "business";
                  return (
                    <List.Item
                      style={{
                        border: "none",
                        paddingBottom: 8,
                        paddingTop: 8,
                        justifyContent: isBusiness ? "flex-end" : "flex-start",
                      }}
                    >
                      <Space
                        direction="horizontal"
                        align="start"
                        style={{
                          maxWidth: "80%",
                          display: "flex",
                          flexDirection: isBusiness ? "row-reverse" : "row",
                        }}
                      >
                        <Avatar
                          icon={isBusiness ? <RobotOutlined /> : <UserOutlined />}
                          style={{
                            backgroundColor: isBusiness ? "#1677ff" : "#87d068",
                          }}
                        />
                        <div
                          style={{
                            backgroundColor: isBusiness ? "#1677ff" : "#f5f5f5",
                            color: isBusiness ? "#fff" : "#000",
                            padding: "8px 12px",
                            borderRadius: 8,
                            maxWidth: 360,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          <Text style={{ color: "inherit" }}>{msg.content}</Text>
                          <div style={{ marginTop: 4, textAlign: "right" }}>
                            <Text
                              style={{
                                fontSize: 10,
                                opacity: 0.8,
                                color: isBusiness ? "#e6f4ff" : "#999",
                              }}
                            >
                              {msg.time}
                            </Text>
                          </div>
                        </div>
                      </Space>
                    </List.Item>
                  );
                }}
              />
            </Card>

            {/* 下部分：你的应用中的发送消息区 */}
            <Card size="small" bodyStyle={{ padding: 12 }}>
              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <Text>
                  {t.sendingTo}{" "}
                  <Text strong>
                    {contacts.find((c) => c.id === selectedContactId)?.name}
                  </Text>
                </Text>
                <Text strong>{t.messageLabel}</Text>
                <TextArea
                  rows={3}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder={t.messagePlaceholder}
                />

                <Space>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={sending}
                    onClick={handleSendMessage}
                  >
                    {t.sendButton}
                  </Button>
                  <Text type="secondary">
                    点击后会在上方聊天窗口中追加一条收到的消息，并自动生成客户回复。
                  </Text>
                </Space>

                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text strong>{t.quickTemplateTitle}</Text>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="选择一个模板自动填充消息内容"
                    value={selectedTemplateId ?? undefined}
                    onChange={(id) => {
                      setSelectedTemplateId(id);
                      const tpl = templates.find((t) => t.id === id);
                      if (tpl) {
                        setInputMessage(tpl.body);
                      }
                    }}
                    allowClear
                  >
                    {templates.map((t) => (
                      <Select.Option key={t.id} value={t.id}>
                        {t.name}（{t.category}）
                      </Select.Option>
                    ))}
                  </Select>
                </Space>
              </Space>
            </Card>
          </div>
        </div>
      </Card>
    </Space>
  );

  const templateTab = (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <Card
        title={
          <Space>
            <PlusOutlined />
            <span>{t.templateCardTitle}</span>
          </Space>
        }
      >

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.1fr 1.3fr",
            gap: 24,
            marginTop: 24,
          }}
        >
          {/* 左侧：创建模板表单 */}
          <Card size="small" title={t.templateCreateTitle}>
            <Form<Template>
              layout="vertical"
              form={templateForm}
              onFinish={(values) =>
                handleCreateTemplate({
                  name: values.name,
                  category: values.category,
                  language: values.language,
                  body: values.body,
                })
              }
              initialValues={{
                category: "TRANSACTIONAL",
                language: "zh_CN",
              }}
            >
              <Form.Item
                label={t.templateNameLabel}
                name="name"
                rules={[{ required: true, message: "请输入模板名称" }]}
              >
                <Input placeholder={t.templateNamePlaceholder} />
              </Form.Item>

              <Form.Item
                label={t.templateCategoryLabel}
                name="category"
                rules={[{ required: true, message: "请选择模板类型" }]}
              >
                <Select>
                  <Select.Option value="TRANSACTIONAL">
                    TRANSACTIONAL（交易类）
                  </Select.Option>
                  <Select.Option value="MARKETING">
                    MARKETING（营销类）
                  </Select.Option>
                  <Select.Option value="UTILITY">
                    UTILITY（工具类）
                  </Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label={t.templateLanguageLabel}
                name="language"
                rules={[{ required: true, message: "请选择语言" }]}
              >
                <Select>
                  <Select.Option value="zh_CN">简体中文（zh_CN）</Select.Option>
                  <Select.Option value="en_US">英语（en_US）</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label={t.templateBodyLabel}
                name="body"
                rules={[{ required: true, message: "请输入模板正文内容" }]}
              >
                <TextArea
                  rows={4}
                  placeholder={t.templateBodyPlaceholder}
                />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                  {t.templateCreateButton}
                </Button>
                <Button
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    void syncTemplatesFromGraph();
                  }}
                >
                  {t.templateSyncButton}
                </Button>
              </Form.Item>
            </Form>
          </Card>

          {/* 右侧：模板列表 + 使用按钮 */}
          <Card size="small" title={t.templateListTitle}>
            <List
              dataSource={templates}
              bordered
              renderItem={(tpl) => (
                <List.Item
                  actions={[
                    <Button
                      key="use"
                      type="link"
                      onClick={() => handleUseTemplate(tpl)}
                    >
                      使用此模板发送消息
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <Text strong>{tpl.name}</Text>
                        <Tag color="blue">{tpl.category}</Tag>
                        <Tag>{tpl.language}</Tag>
                      </Space>
                    }
                    description={
                      <div style={{ whiteSpace: "pre-wrap" }}>{tpl.body}</div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </div>
      </Card>
    </Space>
  );

  const wabaManageTab = (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Card
        size="small"
        style={{
          borderColor: "transparent",
          background: "#e6f4ff",
        }}
        bodyStyle={{ padding: "8px 16px" }}
      >
        <Text type="secondary">{t.wabaNotice}</Text>
      </Card>

      <Card>
        {/* 顶部二级 Tab：WABA 注册及绑定 / 号码迁移 */}
        <Tabs
          defaultActiveKey="wabaRegister"
          items={[
            {
              key: "wabaRegister",
              label: t.wabaTabInternal,
              children: null,
            }
          ]}
        />

        {/* WABA 列表表头 */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            marginTop: 8,
            marginBottom: 8,
          }}
        >
          <Button
            onClick={() => {
              const wabaIds = Array.from(new Set(wabaBindings.map((x) => x.wabaId)));
              if (wabaIds.length === 0) {
                antdMessage.info(
                  locale === "zh"
                    ? "暂无已绑定的 WABA。"
                    : "No bound WABA found yet."
                );
                return;
              }
              wabaIds.forEach((id) => void fetchWabaPhoneNumbers(id));
            }}
          >
            {t.syncPhoneButton}
          </Button>
        </div>

        <div
          style={{
            marginTop: 8,
            border: "1px solid #f0f0f0",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "1.2fr 1.2fr 0.8fr 1.2fr 1.6fr 1fr 1.2fr 1.2fr 1.4fr 0.8fr",
              background: "#fafafa",
              padding: "8px 12px",
              borderBottom: "1px solid #f0f0f0",
              fontWeight: 500,
              fontSize: 12,
            }}
          >
            <span>WABA ID</span>
            <span>{locale === "zh" ? "账户名称" : "Account name"}</span>
            <span>{locale === "zh" ? "币种" : "Currency"}</span>
            <span>{locale === "zh" ? "主营类目" : "Main category"}</span>
            <span>
              {locale === "zh"
                ? "非印度非香港国家"
                : "Country (non-IN / non-HK)"}
            </span>
            <span>{locale === "zh" ? "状态" : "Status"}</span>
            <span>{locale === "zh" ? "绑定状态" : "Binding status"}</span>
            <span>{locale === "zh" ? "商业平台 ID" : "Business platform ID"}</span>
            <span>
              {locale === "zh" ? "商业平台名称" : "Business platform name"}
            </span>
            <span>{locale === "zh" ? "操作" : "Actions"}</span>
          </div>
          {wabaBindings.length === 0 ? (
            <div
              style={{
                padding: "24px 12px",
                textAlign: "center",
                color: "rgba(0,0,0,0.45)",
                fontSize: 13,
              }}
            >
              {locale === "zh" ? "暂无数据" : "No data"}
            </div>
          ) : (
            wabaBindings.map((item) => (
              (() => {
                const phoneList = wabaPhoneNumbersByWaba[item.wabaId] ?? [];
                const matched = phoneList.find((p) => String(p.id) === item.phoneNumberId);
                const accountName = matched?.verified_name || "WhatsApp Business Account";
                return (
              <div
                key={`${item.wabaId}-${item.phoneNumberId}`}
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "1.2fr 1.2fr 0.8fr 1.2fr 1.6fr 1fr 1.2fr 1.2fr 1.4fr 0.8fr",
                  padding: "10px 12px",
                  borderBottom: "1px solid #f0f0f0",
                  fontSize: 12,
                  alignItems: "center",
                }}
              >
                <span>{item.wabaId}</span>
                <span>{accountName}</span>
                <span>USD</span>
                <span>-</span>
                <span>-</span>
                <span>
                  <Tag color="green">已启用</Tag>
                </span>
                <span>
                  <Tag color="blue">已绑定</Tag>
                </span>
                <span>-</span>
                <span>-</span>
                <span>
                  <Button
                    size="small"
                    danger
                    onClick={() =>
                      persistWabaBindings((prev) =>
                        prev.filter(
                          (x) =>
                            !(
                              x.wabaId === item.wabaId &&
                              x.phoneNumberId === item.phoneNumberId
                            )
                        )
                      )
                    }
                  >
                    删除
                  </Button>
                </span>
              </div>
                );
              })()
            ))
          )}
        </div>

        {/* 号码管理区域 */}
        <div style={{ marginTop: 32 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <Text strong>{t.wabaNumberManageTitle}</Text>
            <Space>
              <Button type="primary" onClick={handleOpenAuthModal}>
                {t.wabaBindButton}
              </Button>
            </Space>
          </div>

          <div
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "1.2fr 1.2fr 1fr 1fr 1.2fr 1.2fr 1.2fr 1.2fr",
                background: "#fafafa",
                padding: "8px 12px",
                borderBottom: "1px solid #f0f0f0",
                fontWeight: 500,
                fontSize: 12,
              }}
            >
              <span>{locale === "zh" ? "商务号码" : "Business number"}</span>
              <span>{locale === "zh" ? "号码名称" : "Number name"}</span>
              <span>{locale === "zh" ? "号码状态" : "Status"}</span>
              <span>{locale === "zh" ? "资质评分" : "Quality rating"}</span>
              <span>{locale === "zh" ? "发送限制" : "Sending limit"}</span>
              <span>{locale === "zh" ? "验证状态" : "Verification status"}</span>
              <span>{locale === "zh" ? "更多设置" : "More settings"}</span>
              <span>{locale === "zh" ? "操作" : "Actions"}</span>
            </div>
            {wabaBindings.length === 0 ? (
              <div
                style={{
                  padding: "24px 12px",
                  textAlign: "center",
                  color: "rgba(0,0,0,0.45)",
                  fontSize: 13,
                }}
              >
              {locale === "zh" ? "暂无数据" : "No data"}
              </div>
            ) : (
              wabaBindings.map((item) => (
                (() => {
                  const phoneList = wabaPhoneNumbersByWaba[item.wabaId] ?? [];
                  const matched = phoneList.find(
                    (p) => String(p.id) === item.phoneNumberId
                  );
                  const displayPhone =
                    matched?.display_phone_number || item.phoneNumberId;
                  const name = matched?.verified_name || "WhatsApp 号码";
                  const status = matched?.status || "-";
                  const quality = matched?.quality_rating || "-";
                  const codeStatus = matched?.code_verification_status || "-";
                  return (
                <div
                  key={`phone-${item.phoneNumberId}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "1.2fr 1.2fr 1fr 1fr 1.2fr 1.2fr 1.2fr 1.2fr",
                    padding: "10px 12px",
                    borderBottom: "1px solid #f0f0f0",
                    fontSize: 12,
                    alignItems: "center",
                  }}
                >
                  <span>{displayPhone}</span>
                  <span>{name}</span>
                  <span>
                    <Tag color="green">可用</Tag>
                  </span>
                  <span>{quality}</span>
                  <span>-</span>
                  <span>
                    <Tag color="blue">已验证</Tag>
                  </span>
                  <span>{codeStatus}</span>
                  <span>-</span>
                </div>
                  );
                })()
              ))
            )}
          </div>
        </div>
      </Card>
    </Space>
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "#fff",
        }}
      >
        <Space>
          <MessageOutlined />
          <Title level={4} style={{ color: "#fff", margin: 0 }}>
            {t.appTitle}
          </Title>
        </Space>
        <Select
          size="small"
          value={locale}
          style={{ width: 140 }}
          onChange={(v) => setLocale(v as Locale)}
        >
          <Select.Option value="en">English</Select.Option>
          <Select.Option value="zh">中文</Select.Option>
        </Select>
      </Header>
      <Content style={{ padding: 24 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "waba",
              label: t.tabWaba,
              children: wabaManageTab,
            },
            {
              key: "chat",
              label: t.tabChat,
              children: chatTab,
            },
            {
              key: "templates",
              label: t.tabTemplates,
              children: templateTab,
            },
          ]}
        />

        <Modal
          open={authModalVisible}
          title="选择注册类型"
          onCancel={() => setAuthModalVisible(false)}
          width={720}
          footer={
            <Space style={{ justifyContent: "flex-end", width: "100%" }}>
              <Button onClick={() => setAuthModalVisible(false)}>取消</Button>
              <Button type="primary" onClick={handleAuthNext}>
                下一步
              </Button>
            </Space>
          }
        >
          <div
            style={{
              display: "flex",
              gap: 24,
              justifyContent: "center",
              marginTop: 16,
            }}
          >
            <Card
              hoverable
              style={{
                width: 260,
                borderColor:
                  selectedAuthType === "waba" ? "#52c41a" : "rgba(0,0,0,0.06)",
                borderWidth: 2,
              }}
              onClick={() => setSelectedAuthType("waba")}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 180,
                  gap: 16,
                }}
              >
                <Avatar
                  size={80}
                  style={{ backgroundColor: "#52c41a" }}
                  icon={<MessageOutlined />}
                />
                <Text strong>WhatsApp 商业号码（WABA）</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  使用现有 WhatsApp 商业号码完成授权
                </Text>
              </div>
            </Card>

            <Card
              hoverable
              style={{
                width: 260,
                borderColor:
                  selectedAuthType === "app" ? "#52c41a" : "rgba(0,0,0,0.06)",
                borderWidth: 2,
              }}
              onClick={() => setSelectedAuthType("app")}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 180,
                  gap: 16,
                }}
              >
                <Avatar
                  size={80}
                  style={{ backgroundColor: "#13c2c2" }}
                  icon={<MessageOutlined />}
                />
                <Text strong>WhatsApp 商业应用</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  暂未在本功能中实现，仅用于界面展示
                </Text>
              </div>
            </Card>
          </div>
        </Modal>
      </Content>
    </Layout>
  );
}

export default App;